import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { getConfig } from './storage'
import { descricaoMovel, formatTelefone } from './fmt'

const INK    = [17, 17, 17]
const GOLD   = [245, 194, 0]
const GOLD_L = [255, 249, 194]
const GRAY   = [100, 116, 139]
const BGROW  = [248, 250, 252]
const WHITE  = [255, 255, 255]

const FORMAS_LABEL = {
  pix: 'Pix', dinheiro: 'Dinheiro', credito: 'Cartão de crédito', debito: 'Cartão de débito',
}
const PRAZOS_LABEL = {
  ato: 'Tudo no ato', '50_50': '50% no pedido + 50% na entrega',
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)
}
function formatDate(dateStr) {
  if (!dateStr) return '—'
  try { return format(new Date(dateStr + 'T12:00:00'), "dd/MM/yyyy", { locale: ptBR }) } catch { return dateStr }
}

// Desenha um bloco de seção com título e fundo cinza claro
function sectionHeader(doc, label, y, pageW, marginL, marginR) {
  doc.setFillColor(...BGROW)
  doc.roundedRect(marginL, y - 4.5, pageW - marginL - marginR, 7, 1.5, 1.5, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7.5)
  doc.setTextColor(...GRAY)
  doc.text(label.toUpperCase(), marginL + 3, y)
  return y + 5
}

// Par label → valor numa linha
function infoRow(doc, label, value, x, y, labelW) {
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.setTextColor(...GRAY)
  doc.text(label, x, y)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...INK)
  doc.text(String(value || '—'), x + labelW, y)
}

export function gerarPDF(pedido) {
  const config = getConfig()
  const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const pageW  = doc.internal.pageSize.getWidth()
  const pageH  = doc.internal.pageSize.getHeight()
  const mL     = 14
  const mR     = 14

  // ── Cabeçalho ────────────────────────────────────────────────────────────
  doc.setFillColor(...INK)
  doc.rect(0, 0, pageW, 28, 'F')

  // Barra dourada fina na base do header
  doc.setFillColor(...GOLD)
  doc.rect(0, 26, pageW, 2, 'F')

  // Nome empresa
  doc.setTextColor(...WHITE)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  doc.text('FMINI', mL, 13)

  doc.setTextColor(...GOLD)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text('Marcenaria', mL + 27, 13)

  doc.setTextColor(180, 180, 180)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  doc.text('Móveis Planejados sob Medida', mL, 19)

  // Número e data — direita
  doc.setTextColor(...GOLD)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text(`Pedido ${pedido.numero}`, pageW - mR, 12, { align: 'right' })

  doc.setTextColor(200, 200, 200)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  doc.text(`Emitido em ${formatDate(new Date().toISOString().split('T')[0])}`, pageW - mR, 18, { align: 'right' })
  doc.text(`Prazo de entrega: ${formatDate(pedido.prazo)}`, pageW - mR, 23, { align: 'right' })

  // ── Bloco de status (pill) ────────────────────────────────────────────────
  let y = 36

  // ── Seção: Cliente ────────────────────────────────────────────────────────
  y = sectionHeader(doc, 'Dados do cliente', y, pageW, mL, mR)
  y += 2

  const col2 = pageW / 2 + 4
  const lw   = 18  // label width

  infoRow(doc, 'Nome:', pedido.cliente.nome, mL, y, lw)
  infoRow(doc, 'Documento:', `${pedido.cliente.documento} (${pedido.cliente.tipoDocumento})`, col2, y, 24)
  y += 6

  infoRow(doc, 'Contato:', formatTelefone(pedido.cliente.contato), mL, y, lw)
  infoRow(doc, 'E-mail:', pedido.cliente.email || '—', col2, y, 14)
  y += 6

  const enderecoLines = doc.splitTextToSize(pedido.cliente.endereco || '—', pageW - mL - mR - lw)
  infoRow(doc, 'Endereço:', enderecoLines[0], mL, y, lw)
  if (enderecoLines.length > 1) {
    y += 5
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...INK)
    doc.setFontSize(8)
    doc.text(enderecoLines.slice(1).join(' '), mL + lw, y)
  }
  y += 8

  // ── Seção: Pedido ────────────────────────────────────────────────────────
  y = sectionHeader(doc, 'Informações do pedido', y, pageW, mL, mR)
  y += 2

  infoRow(doc, 'Prazo:', formatDate(pedido.prazo), mL, y, lw)
  infoRow(doc, 'Status:', (pedido.status || '').replace(/_/g, ' '), col2, y, 14)
  y += 6

  if (pedido.vendedora || pedido.formaPagamento) {
    if (pedido.vendedora)       infoRow(doc, 'Vendedora:', pedido.vendedora, mL, y, lw)
    if (pedido.formaPagamento)  infoRow(doc, 'Pagamento:', FORMAS_LABEL[pedido.formaPagamento] || pedido.formaPagamento, col2, y, 22)
    y += 6
  }
  if (pedido.prazoPagamento) {
    const pl = pedido.prazoPagamento === 'custom'
      ? pedido.prazoPagamentoCustom || 'Personalizado'
      : PRAZOS_LABEL[pedido.prazoPagamento] || pedido.prazoPagamento
    infoRow(doc, 'Condição:', pl, mL, y, lw)
    y += 6
  }
  y += 4

  // ── Seção: Móveis ─────────────────────────────────────────────────────────
  y = sectionHeader(doc, `Móveis do pedido (${pedido.moveis.length})`, y, pageW, mL, mR)
  y += 2

  const rows = pedido.moveis.map((m, i) => [
    i + 1,
    descricaoMovel(m),
    formatCurrency(m.preco),
  ])

  autoTable(doc, {
    startY: y,
    head: [['#', 'Descrição', 'Preço']],
    body: rows,
    styles:     { fontSize: 8, cellPadding: 3, textColor: INK },
    headStyles: { fillColor: INK, textColor: GOLD, fontStyle: 'bold', fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 8,  halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 22, halign: 'right', fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: BGROW },
    margin: { left: mL, right: mR },
  })

  let afterTable = doc.lastAutoTable.finalY

  // ── Total ─────────────────────────────────────────────────────────────────
  afterTable += 3
  doc.setFillColor(...GOLD_L)
  doc.roundedRect(pageW - mR - 68, afterTable - 4, 68, 10, 2, 2, 'F')
  doc.setFillColor(...GOLD)
  doc.roundedRect(pageW - mR - 68, afterTable - 4, 68, 10, 2, 2, 'S')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(...INK)
  if (pedido.desconto > 0) {
    const totalBruto = pedido.valorTotal + pedido.desconto
    doc.text('Subtotal', pageW - mR - 65, afterTable + 2)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8.5)
    doc.text(formatCurrency(totalBruto), pageW - mR - 2, afterTable + 2, { align: 'right' })
    afterTable += 6
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8.5)
    doc.setTextColor(22, 163, 74)
    const pct = (pedido.desconto / totalBruto * 100).toFixed(1)
    doc.text(`Desconto (${pct}%)`, pageW - mR - 65, afterTable + 2)
    doc.text(`− ${formatCurrency(pedido.desconto)}`, pageW - mR - 2, afterTable + 2, { align: 'right' })
    afterTable += 6
    doc.setFillColor(...GOLD_L)
    doc.roundedRect(pageW - mR - 68, afterTable - 4, 68, 10, 2, 2, 'F')
    doc.setFillColor(...GOLD)
    doc.roundedRect(pageW - mR - 68, afterTable - 4, 68, 10, 2, 2, 'S')
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.setTextColor(...INK)
    doc.text('TOTAL DO PEDIDO', pageW - mR - 65, afterTable + 2)
    doc.setFontSize(10)
    doc.text(formatCurrency(pedido.valorTotal), pageW - mR - 2, afterTable + 2, { align: 'right' })
  } else {
    doc.text('TOTAL DO PEDIDO', pageW - mR - 65, afterTable + 2)
    doc.setFontSize(10)
    doc.text(formatCurrency(pedido.valorTotal), pageW - mR - 2, afterTable + 2, { align: 'right' })
  }

  // ── Observações gerais ────────────────────────────────────────────────────
  if (pedido.observacoesGerais) {
    afterTable += 16
    afterTable = sectionHeader(doc, 'Observações gerais', afterTable, pageW, mL, mR)
    afterTable += 2
    doc.setFont('helvetica', 'italic')
    doc.setFontSize(8)
    doc.setTextColor(...INK)
    const obsLines = doc.splitTextToSize(`"${pedido.observacoesGerais}"`, pageW - mL - mR)
    doc.text(obsLines, mL, afterTable)
  }

  // ── Rodapé ────────────────────────────────────────────────────────────────
  doc.setFillColor(...INK)
  doc.rect(0, pageH - 14, pageW, 14, 'F')
  doc.setFillColor(...GOLD)
  doc.rect(0, pageH - 14, pageW, 1.5, 'F')

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7)
  doc.setTextColor(180, 180, 180)
  doc.text('MINI Marcenaria · Móveis Planejados sob Medida', mL, pageH - 7)
  doc.text(`Documento gerado automaticamente em ${formatDate(new Date().toISOString().split('T')[0])}`, pageW - mR, pageH - 7, { align: 'right' })

  if (config.googleSheetsViewUrl) {
    doc.setTextColor(...GOLD)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    doc.textWithLink('↗ Abrir Planilha de Pedidos', pageW / 2, pageH - 4, {
      align: 'center',
      url: config.googleSheetsViewUrl,
    })
  }

  doc.save(`Pedido-${pedido.numero}.pdf`)
}
